import { getFunctions, httpsCallable } from "firebase/functions";

export const BOOKING_REQUEST_RESOURCE = "booking-requests";

export type BookingRequestStatus =
  | "new"
  | "contacted"
  | "approved"
  | "rejected";

export interface CommunicationLogEntry {
  type: string;
  subject: string;
  sentAt?: { seconds: number } | string | null;
  sentBy?: string;
}

export interface BookingRequestRecord {
  id: string;
  name: string;
  email: string;
  phone: string;
  requestType: string;
  preferredDate: string; // YYYY-MM-DD
  preferredTime?: string; // HH:mm
  guestCount?: string;
  message?: string;
  status?: BookingRequestStatus;
  scheduledDate?: string;
  scheduledTime?: string;
  adminNotes?: string;
  communications?: CommunicationLogEntry[];
  createdAt?: { seconds: number } | string | null;
}

export type BookingRequestView =
  | "open"
  | "expired"
  | "approved"
  | "rejected"
  | "all";

const OPEN_STATUSES: BookingRequestStatus[] = ["new", "contacted"];

export const todayDateString = (now: Date = new Date()): string => {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const statusOf = (record: BookingRequestRecord): BookingRequestStatus =>
  record.status || "new";

export const isExpired = (
  record: BookingRequestRecord,
  today: string = todayDateString()
): boolean =>
  OPEN_STATUSES.includes(statusOf(record)) &&
  !!record.preferredDate &&
  record.preferredDate < today;

// The status shown to admins: unresolved requests whose preferred date has
// passed are displayed as "expired" even though the stored status is unchanged.
export const getEffectiveStatus = (
  record: BookingRequestRecord,
  today: string = todayDateString()
): BookingRequestStatus | "expired" =>
  isExpired(record, today) ? "expired" : statusOf(record);

export const matchesView = (
  record: BookingRequestRecord,
  view: BookingRequestView,
  today: string = todayDateString()
): boolean => {
  switch (view) {
    case "open":
      return OPEN_STATUSES.includes(statusOf(record)) && !isExpired(record, today);
    case "expired":
      return isExpired(record, today);
    case "approved":
      return statusOf(record) === "approved";
    case "rejected":
      return statusOf(record) === "rejected";
    case "all":
    default:
      return true;
  }
};

export const filterBookingRequestsByView = <T extends BookingRequestRecord>(
  records: T[],
  view: BookingRequestView | undefined,
  today: string = todayDateString()
): T[] => {
  if (!view || view === "all") {
    return records;
  }
  return records.filter((record) => matchesView(record, view, today));
};

export type BookingRequestAction = "contact" | "approve" | "reject";

export interface RespondToBookingRequestInput {
  requestId: string;
  action: BookingRequestAction;
  subject?: string;
  message?: string;
  scheduledDate?: string;
  scheduledTime?: string;
}

export interface RespondToBookingRequestResponse {
  success: boolean;
  message: string;
}

export async function respondToBookingRequest(
  input: RespondToBookingRequestInput
): Promise<RespondToBookingRequestResponse> {
  const callable = httpsCallable<
    RespondToBookingRequestInput,
    RespondToBookingRequestResponse
  >(getFunctions(), "respondToBookingRequest");

  const result = await callable(input);
  return result.data;
}
